import { Box, Text, useColorModeValue } from '@chakra-ui/react';

import {
  type ScoreFamily,
  scoreFamilyMeta,
} from '../../constants/scoreFamilies';
import type { ProfitRatingBoxAppearance } from '../../utils/profitRatingColors';

export type ScoreShortLabel = 'PR' | 'SPS' | 'PPS' | 'WCS' | 'ROI';

type ScoreValueBoxProps = {
  family: ScoreFamily;
  shortLabel: ScoreShortLabel;
  value: string | number;
  size?: 'xs' | 'sm' | 'md' | 'lg';
  /** Optional override for gradient / scaled PR styling. */
  appearance?: ProfitRatingBoxAppearance;
  /** Stretch to parent width (profile period columns). */
  fillWidth?: boolean;
  /** Hide the inner short label when a column header already shows it. */
  hideShortLabel?: boolean;
  /** Profile hero row — equal height, ~2× vertical padding, centered value. */
  heroMetric?: boolean;
  /** Table cells: thinner border, no shadow. */
  compact?: boolean;
};

/** Cap font size (rem) for each size / hero variant. */
function maxScoreRem(
  size: NonNullable<ScoreValueBoxProps['size']>,
  heroMetric: boolean,
): string {
  if (heroMetric) {
    if (size === 'xs') return '1.25rem';
    if (size === 'sm') return '1.5rem';
    return '1.875rem';
  }
  if (size === 'xs') return '0.875rem';
  if (size === 'sm') return '1.125rem';
  if (size === 'lg') return '1.875rem';
  return '1.5rem';
}

export function ScoreValueBox({
  family,
  shortLabel,
  value,
  size = 'md',
  appearance,
  fillWidth = false,
  hideShortLabel = false,
  heroMetric = false,
  compact = false,
}: ScoreValueBoxProps) {
  const meta = scoreFamilyMeta(family);
  const defaultScoreColor = useColorModeValue(
    meta.valueColorLight,
    meta.valueColorDark,
  );
  const defaultBoxBg = useColorModeValue(meta.boxBgLight, meta.boxBgDark);
  const defaultBorderColor = useColorModeValue(
    meta.borderToken,
    meta.borderToken,
  );
  const scoreColor = appearance?.scoreColor ?? defaultScoreColor;
  const borderColor = appearance?.borderColor ?? defaultBorderColor;
  const muted = useColorModeValue('gray.600', 'gray.400');

  const boxPadding = heroMetric
    ? size === 'xs'
      ? { py: 2, px: 1.25 }
      : size === 'sm'
        ? { py: 2.5, px: 1.5 }
        : { py: 4, px: 2.5 }
    : size === 'xs'
      ? { py: fillWidth ? 2 : 0.5, px: 1.25 }
      : size === 'sm'
        ? { py: fillWidth ? 2 : 1, px: 1.5 }
        : size === 'lg'
          ? { py: 3, px: 2.5 }
          : { py: fillWidth ? 2.5 : 2, px: 2.5 };
  const boxMinW =
    size === 'xs'
      ? '2.75rem'
      : size === 'sm'
        ? '3.5rem'
        : size === 'lg'
          ? '5.5rem'
          : '4.75rem';
  const baseScoreFontSize = heroMetric
    ? size === 'xs'
      ? 'xl'
      : size === 'sm'
        ? '2xl'
        : '3xl'
    : size === 'xs'
      ? 'sm'
      : size === 'sm'
        ? 'lg'
        : size === 'lg'
          ? '3xl'
          : '2xl';
  const labelMargin = size === 'xs' ? 0.25 : 0.5;

  const displayValue =
    typeof value === 'number' ? value.toFixed(1) : String(value);
  // Scale long values (e.g. +3266.7%) to the content width via container queries.
  const fitToContainer = fillWidth || heroMetric;
  const charCount = Math.max(displayValue.length, 1);
  // Extrabold tabular +/% run wider than 0.62em; use 0.75 so long ROIs fit.
  // Measure on an inner full-width wrapper (not the padded outer box) so cqw
  // matches the text area.
  const fitFontSize = `clamp(0.625rem, calc(100cqw / ${charCount * 0.75}), ${maxScoreRem(size, heroMetric)})`;

  const heroMinH = size === 'xs' ? '4.25rem' : '8rem';
  // Period / fill-width boxes need room so scaled ROI text doesn't feel cramped.
  const fillMinH =
    fillWidth && !heroMetric
      ? size === 'xs'
        ? '3rem'
        : size === 'lg'
          ? '4.25rem'
          : '3.5rem'
      : undefined;

  const valueText = (
    <Text
      fontSize={fitToContainer ? undefined : baseScoreFontSize}
      fontWeight="extrabold"
      color={scoreColor}
      lineHeight="1"
      whiteSpace="nowrap"
      maxW="100%"
      overflow="hidden"
      textAlign="center"
      sx={{
        fontVariantNumeric: 'tabular-nums',
        ...(fitToContainer ? { fontSize: fitFontSize } : undefined),
      }}
    >
      {displayValue}
    </Text>
  );

  return (
    <Box
      textAlign="center"
      w={fitToContainer ? 'full' : 'fit-content'}
      h={heroMetric ? '100%' : undefined}
      minH={heroMetric ? heroMinH : fillMinH}
      display={fitToContainer ? 'flex' : undefined}
      flexDirection={fitToContainer ? 'column' : undefined}
      justifyContent={fitToContainer ? 'center' : undefined}
      alignItems={fitToContainer ? 'center' : undefined}
      {...boxPadding}
      minW={fitToContainer ? 0 : boxMinW}
      maxW={fitToContainer ? '100%' : undefined}
      overflow={fitToContainer ? 'hidden' : undefined}
      borderRadius="md"
      borderWidth={compact ? '1px' : '2px'}
      borderColor={borderColor}
      bg={appearance ? undefined : defaultBoxBg}
      boxShadow={compact ? undefined : 'sm'}
      sx={
        appearance ? { backgroundImage: appearance.backgroundImage } : undefined
      }
    >
      {hideShortLabel ? null : (
        <Text
          fontSize="2xs"
          fontWeight="bold"
          color={muted}
          textTransform="uppercase"
          letterSpacing="wider"
          lineHeight="1"
          mb={labelMargin}
        >
          {shortLabel}
        </Text>
      )}
      {fitToContainer ? (
        <Box
          w="100%"
          minW={0}
          overflow="hidden"
          sx={{ containerType: 'inline-size' }}
        >
          {valueText}
        </Box>
      ) : (
        valueText
      )}
    </Box>
  );
}
